import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import PermissionGate from './PermissionGate';
import PropertyPhotoUploader from './PropertyPhotoUploader';
import PropertyPhotoLightboxModal from './PropertyPhotoLightboxModal';
import {
  getPropertyImages,
  deletePropertyImage,
  setAsDefaultImage,
  reorderPropertyImages,
} from '../services/propertyImageService';
import { IMAGE_TYPE_ORDER, IMAGE_TYPE_CONFIG, getImageTypeConfig } from '../utils/propertyUtils';

/**
 * PropertyPhotosTab
 *
 * Props:
 *   property       {object}   full property object (must have .id)
 *   agencyId       {number}
 *   onPhotoChange  {function} called after any create/delete/set-default so parent can refresh
 */
const PropertyPhotosTab = ({ property, agencyId, onPhotoChange }) => {
  const { } = useContext(AuthContext); // ensure auth context is available

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filter, setFilter] = useState(null); // null = all
  const [showUploader, setShowUploader] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null); // index into filteredImages

  // Drag and drop reorder state
  const dragIndexRef = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const reorderTimerRef = useRef(null);

  // ─── Fetch ───────────────────────────────────────────────────────────────────
  const fetchImages = useCallback(async () => {
    if (!property?.id || !agencyId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getPropertyImages(agencyId, property.id);
      setImages(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [agencyId, property?.id]);

  useEffect(() => {
    fetchImages();
    return () => {
      if (reorderTimerRef.current) clearTimeout(reorderTimerRef.current);
    };
  }, [fetchImages]);

  // ─── Filtered view ───────────────────────────────────────────────────────────
  const filteredImages = filter
    ? images.filter((img) => img.image_type === filter)
    : images;

  // ─── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = useCallback(async (image) => {
    if (!window.confirm('Remove this photo? The image will be hidden from the property gallery.')) return;

    try {
      await deletePropertyImage(agencyId, property.id, image.id);
      setImages((prev) => prev.filter((img) => img.id !== image.id));
      // Close lightbox if we deleted the currently viewed image
      setLightboxIndex(null);
      if (onPhotoChange) onPhotoChange();
    } catch (err) {
      alert(`Failed to remove photo: ${err.message}`);
    }
  }, [agencyId, property?.id, onPhotoChange]);

  // ─── Set default ─────────────────────────────────────────────────────────────
  const handleSetDefault = useCallback(async (image) => {
    try {
      await setAsDefaultImage(agencyId, property.id, image.id);
      // Update local state: clear all is_default, set the new one
      setImages((prev) =>
        prev.map((img) => ({
          ...img,
          is_default: img.id === image.id,
        }))
      );
      setLightboxIndex(null);
      if (onPhotoChange) onPhotoChange();
    } catch (err) {
      alert(`Failed to set cover photo: ${err.message}`);
    }
  }, [agencyId, property?.id, onPhotoChange]);

  // ─── Reorder (drag and drop) ─────────────────────────────────────────────────
  const flushReorder = useCallback(async (orderedImages) => {
    try {
      const imageOrders = {};
      orderedImages.forEach((img, idx) => {
        imageOrders[String(img.id)] = idx;
      });
      await reorderPropertyImages(agencyId, property.id, imageOrders);
    } catch (err) {
      console.error('[PropertyPhotosTab] Reorder save failed:', err.message);
      // Silent fail — optimistic update already applied; will correct on next refresh
    }
  }, [agencyId, property?.id]);

  const handleDragStart = (e, index) => {
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);

    const fromIndex = dragIndexRef.current;
    dragIndexRef.current = null;

    if (fromIndex === null || fromIndex === dropIndex) return;

    // Reorder the full `images` array (not the filtered view — filter must be off for drag)
    const reordered = [...images];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(dropIndex, 0, moved);

    setImages(reordered);

    // Debounced save — 500 ms after last drop
    if (reorderTimerRef.current) clearTimeout(reorderTimerRef.current);
    reorderTimerRef.current = setTimeout(() => flushReorder(reordered), 500);
  };

  const handleDragEnd = () => {
    setDragOverIndex(null);
    dragIndexRef.current = null;
  };

  // ─── Uploader callbacks ──────────────────────────────────────────────────────
  const handleUploadComplete = () => {
    setShowUploader(false);
    fetchImages();
    if (onPhotoChange) onPhotoChange();
  };

  // ─── Skeleton tiles ───────────────────────────────────────────────────────────
  const renderSkeletons = () =>
    Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="col">
        <div
          className="rounded-3 placeholder-glow"
          style={{ paddingBottom: '100%', position: 'relative', overflow: 'hidden' }}
        >
          <span
            className="placeholder rounded-3"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          ></span>
        </div>
      </div>
    ));

  // ─── Empty state ─────────────────────────────────────────────────────────────
  const renderEmpty = () => (
    <div className="text-center py-5">
      <i className="bi bi-images fs-1 text-muted d-block mb-3"></i>
      <h6 className="text-muted fw-semibold mb-1">
        {filter ? `No ${IMAGE_TYPE_CONFIG[filter]?.label ?? filter} photos` : 'No photos yet'}
      </h6>
      <p className="text-muted small mb-3">
        {filter
          ? 'Try a different type filter or upload photos of this type.'
          : 'Upload photos to showcase this property.'}
      </p>
      <PermissionGate requirePermission="properties.manage">
        <button
          type="button"
          className="btn btn-light btn-sm"
          onClick={() => setShowUploader(true)}
        >
          <i className="bi bi-cloud-upload me-2"></i>Upload first photo
        </button>
      </PermissionGate>
    </div>
  );

  // ─── Photo card ───────────────────────────────────────────────────────────────
  const renderCard = (image, index) => {
    const thumb = image.file?.medium_url || image.file?.file_url || '';
    const typeConfig = getImageTypeConfig(image.image_type);
    const isProfile = image.image_type === 'profile';
    const isDraggingOver = !filter && dragOverIndex === index;

    return (
      <div
        key={image.id}
        className="col"
        draggable={!filter}
        onDragStart={!filter ? (e) => handleDragStart(e, index) : undefined}
        onDragOver={!filter ? (e) => handleDragOver(e, index) : undefined}
        onDragLeave={!filter ? handleDragLeave : undefined}
        onDrop={!filter ? (e) => handleDrop(e, index) : undefined}
        onDragEnd={!filter ? handleDragEnd : undefined}
        style={{ cursor: !filter ? 'grab' : 'default' }}
      >
        <div
          className="position-relative rounded-3 overflow-hidden shadow-sm"
          style={{
            paddingBottom: '100%',
            outline: isDraggingOver ? '2px dashed #6610f2' : 'none',
            opacity: dragIndexRef.current === index && !filter ? 0.5 : 1,
            transition: 'opacity 0.15s, outline 0.1s',
          }}
        >
          {/* Thumbnail */}
          {thumb ? (
            <img
              src={thumb}
              alt={image.file?.file_name || 'Property photo'}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              draggable={false}
            />
          ) : (
            <div
              className="d-flex align-items-center justify-content-center bg-light position-absolute"
              style={{ inset: 0 }}
            >
              <i className="bi bi-image text-muted fs-2"></i>
            </div>
          )}

          {/* Type badge — top left */}
          <span
            className={`badge position-absolute top-0 start-0 m-2 ${typeConfig.badgeClass}`}
            style={{ fontSize: '0.65rem' }}
          >
            {typeConfig.label}
          </span>

          {/* Default star — top right (only profile images) */}
          <PermissionGate requirePermission="properties.manage">
            <span
              className="position-absolute top-0 end-0 m-2"
              title={
                !isProfile
                  ? 'Only profile photos can be set as cover'
                  : image.is_default
                  ? 'Current cover photo'
                  : 'Set as cover photo'
              }
            >
              <button
                type="button"
                className="btn btn-sm p-0"
                style={{
                  background: 'rgba(0,0,0,0.45)',
                  border: 'none',
                  borderRadius: '50%',
                  width: 28,
                  height: 28,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isProfile && !image.is_default ? 'pointer' : 'default',
                  opacity: !isProfile ? 0.35 : 1,
                }}
                disabled={!isProfile || image.is_default}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isProfile && !image.is_default) handleSetDefault(image);
                }}
              >
                <i
                  className={`bi ${image.is_default ? 'bi-star-fill text-warning' : 'bi-star text-white'}`}
                  style={{ fontSize: '0.75rem' }}
                ></i>
              </button>
            </span>
          </PermissionGate>

          {/* Hover overlay — view + delete */}
          <div
            className="position-absolute bottom-0 start-0 end-0 d-flex align-items-center justify-content-center gap-2 py-2"
            style={{
              background: 'linear-gradient(to top, rgba(0,0,0,0.65), transparent)',
              opacity: 0,
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = 0)}
          >
            {/* View */}
            <button
              type="button"
              className="btn btn-sm btn-light"
              style={{ fontSize: '0.7rem', padding: '2px 8px' }}
              onClick={() => setLightboxIndex(index)}
              title="View full size"
            >
              <i className="bi bi-arrows-fullscreen me-1"></i>View
            </button>

            {/* Delete */}
            <PermissionGate requirePermission="properties.manage">
              <button
                type="button"
                className="btn btn-sm btn-danger"
                style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                onClick={(e) => { e.stopPropagation(); handleDelete(image); }}
                title="Remove photo"
              >
                <i className="bi bi-trash"></i>
              </button>
            </PermissionGate>
          </div>

          {/* Invisible overlay to make whole card clickable for view */}
          <div
            className="position-absolute"
            style={{ inset: 0, cursor: 'pointer', zIndex: 0 }}
            onClick={() => setLightboxIndex(index)}
            onMouseEnter={(e) => {
              const overlay = e.currentTarget.previousSibling;
              if (overlay) overlay.style.opacity = 1;
            }}
            onMouseLeave={(e) => {
              const overlay = e.currentTarget.previousSibling;
              if (overlay) overlay.style.opacity = 0;
            }}
          ></div>
        </div>

        {/* Caption  */}
        {image.caption && (
          <p className="text-muted small text-truncate mt-1 mb-0 px-1" title={image.caption}>
            {image.caption}
          </p>
        )}
      </div>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <div className="d-flex align-items-center gap-2">
          <i className="bi bi-images text-muted"></i>
          <span className="fw-semibold">Property Photos</span>
          {!loading && (
            <span className="badge bg-secondary">{images.length}</span>
          )}
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2">
          {/* Filter pills */}
          <div className="btn-group btn-group-sm" role="group" aria-label="Filter by type">
            <button
              type="button"
              className={`btn ${filter === null ? 'btn-dark' : 'btn-outline-secondary'}`}
              onClick={() => setFilter(null)}
            >
              All
            </button>
            {IMAGE_TYPE_ORDER.map((type) => (
              <button
                key={type}
                type="button"
                className={`btn ${filter === type ? 'btn-dark' : 'btn-outline-secondary'}`}
                onClick={() => setFilter(filter === type ? null : type)}
              >
                {IMAGE_TYPE_CONFIG[type].filterLabel}
              </button>
            ))}
          </div>

          {/* Upload button */}
          <PermissionGate requirePermission="properties.manage">
            <button
              type="button"
              className="btn btn-light btn-sm"
              onClick={() => setShowUploader((v) => !v)}
            >
              <i className={`bi ${showUploader ? 'bi-chevron-up' : 'bi-cloud-upload'} me-2`}></i>
              {showUploader ? 'Hide uploader' : 'Upload Photos'}
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Drag-to-reorder hint */}
      {!filter && images.length > 1 && (
        <p className="small text-muted mb-3">
          <i className="bi bi-grip-vertical me-1"></i>
          Drag tiles to reorder · changes save automatically · reorder unavailable when a filter is active
        </p>
      )}
      {filter && images.length > 1 && (
        <p className="small text-muted mb-3">
          <i className="bi bi-info-circle me-1"></i>
          Clear the filter to enable drag-and-drop reordering.
        </p>
      )}

      {/* ── Uploader (collapsible) ──────────────────────────────────────────── */}
      {showUploader && (
        <PropertyPhotoUploader
          agencyId={agencyId}
          propertyId={property.id}
          onUploadComplete={handleUploadComplete}
          onCancel={() => setShowUploader(false)}
        />
      )}

      {/* ── Gallery ────────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 g-3">
          {renderSkeletons()}
        </div>
      ) : error ? (
        <div className="alert alert-danger d-flex align-items-center gap-3">
          <i className="bi bi-exclamation-triangle-fill fs-5"></i>
          <div className="flex-grow-1">
            <strong>Failed to load photos</strong>
            <div className="small">{error}</div>
          </div>
          <button
            type="button"
            className="btn btn-outline-danger btn-sm flex-shrink-0"
            onClick={fetchImages}
          >
            <i className="bi bi-arrow-clockwise me-1"></i>Retry
          </button>
        </div>
      ) : filteredImages.length === 0 ? (
        renderEmpty()
      ) : (
        <div className="row row-cols-2 row-cols-md-3 row-cols-lg-4 g-3">
          {filteredImages.map((img, idx) => renderCard(img, idx))}
        </div>
      )}

      {/* ── Lightbox ───────────────────────────────────────────────────────── */}
      <PropertyPhotoLightboxModal
        show={lightboxIndex !== null}
        images={filteredImages}
        currentIndex={lightboxIndex ?? 0}
        onClose={() => setLightboxIndex(null)}
        onNavigate={setLightboxIndex}
        onDelete={handleDelete}
        onSetDefault={handleSetDefault}
      />
    </div>
  );
};

export default PropertyPhotosTab;

